import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

// Store
import AppState from '@webapp/store/state';
import { CreateOperatorInvite } from '@webapp/store/record-labels/action-creators';
import {
  ArtistInviteSource,
  CreateArtistInvite,
} from '@webapp/store/artist/action-creators';

// Types
import { ContactInfo } from '@webapp/types/common/contact-info';

// Services
import { TracInviteTypes } from '@services/invites/types/invite-types';
import { Collaborator } from '@services/collaborator/types/collaborator';
import { NewCollaborator } from '@services/collaborator/types/new-collaborator';
import { SubscriptionType } from '@services/subscription/types/subscription-type';
import { RecordLabelSubscriptionType } from '@services/subscription/types/record-label-subcription';

// Types
import User from '@common/types/user/user';
import { Country } from '@common/types/data/country';
import SimpleUser from '@common/types/user/simple-user';
import SimpleArtist from '@common/types/artist/simple-artist';
import { InviteArtistRequest } from '@common/types/invites/invite-artist-request';
import InviteOperatorRequest from '@common/types/record-label/invite-operator-request';

// Components
import Flex from '@components/layout/flex';
import Button from '@components/actions/button';
import { DEFAULT_COUNTRY } from '../../form/email-or-phone/index';

// Controllers
import EmailOrPhoneCtrl from '@webapp/controllers/form/email-or-phone';
import { CreateCollaborator } from '@webapp/store/collaborator/action-creators';

interface InviteArtistInlineCtrlProps {
  className?: string;
  name: string;
  value?: SimpleArtist | Collaborator | NewCollaborator;
  type: TracInviteTypes;
  role: string;
  songName: string;
  releaseId?: string;
  fromUser?: boolean;
  split?: number;
  labelName?: string;
  labelId?: string;
  inviter?: Partial<User>;
  addedArtists?: SimpleArtist[];
  currentValues?: any[];
  inviteType?: SubscriptionType;
  source?: ArtistInviteSource;
  contact?: ContactInfo;
  showError?: React.ReactNode;
  setShowError?: React.Dispatch<React.SetStateAction<React.ReactNode>>;
  labelPlan: RecordLabelSubscriptionType;
  customBorderRadius?: string;
  canBeWithoutInvite?: boolean;
  onCancel: () => void;
  onInvite: () => void;
  handleEmailOrPhoneChange?: (newContact: ContactInfo) => void;
  inviteOperator: (
    recordLabelId: string,
    invite: InviteOperatorRequest
  ) => CreateOperatorInvite;
  inviteArtist: (
    invite: InviteArtistRequest,
    source?: ArtistInviteSource
  ) => CreateArtistInvite;
  setRole?: React.Dispatch<React.SetStateAction<string>>;
  createCollaborator: (collaborator: NewCollaborator) => CreateCollaborator;
}

interface MappedProps {
  activeArtist?: SimpleArtist;
  activeUser: SimpleUser;
}

const mapStateToProps = (state: AppState) => ({
  activeArtist: state.artist.active,
  activeUser: state.user.profile as SimpleUser,
  inviter: state.user.profile,
  addedArtists: state.release.release?.artists,
  labelPlan: ((state.recordLabels.active?.id &&
    state.subscription.label[
      state.recordLabels.active.id
    ]?.plan?.toUpperCase()) ||
    'FREE') as RecordLabelSubscriptionType,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  inviteOperator: (recordLabelId: string, invite: InviteOperatorRequest) =>
    dispatch(new CreateOperatorInvite(recordLabelId, invite)),
  inviteArtist: (invite: InviteArtistRequest, source?: ArtistInviteSource) =>
    dispatch(new CreateArtistInvite(invite, source)),
  createCollaborator: (collaborator: NewCollaborator) =>
    dispatch(new CreateCollaborator(collaborator)),
});

const InviteArtistInlineCtrl: React.FC<
  InviteArtistInlineCtrlProps & MappedProps
> = ({ inviteType = 'artist', role, setRole, ...props }) => {
  const [contact, setContact] = React.useState<ContactInfo>();
  const [contactFieldValue, setContactFieldValue] = React.useState<string>();
  const [country, setCountry] = React.useState<Country>(DEFAULT_COUNTRY);

  React.useEffect(() => setRole && setRole(role), [role, setRole]);

  const handleInvite = async () => {
    if (props.name && !contactFieldValue && props.canBeWithoutInvite) {
      props.createCollaborator({
        assetId: props.source?.id || '',
        assetType: props.source?.assetType || 'TRACK',
        type: props.source?.type || 'COLLABORATOR',
        name: props.name,
        skipInvite: true,
        collaboratorType: 'ARTIST',
        role: role,
        releaseId: props.releaseId,
      });
      return;
    }

    if (!props.name || !contact) {
      return;
    }

    //Main Artist
    let findEmailMain = props.addedArtists?.some(
      (val) => val.email === contact?.value
    );
    let findTelephoneMain = props.addedArtists?.some(
      (val) => val.telephone === contact?.value
    );
    if (
      (findEmailMain || findTelephoneMain) &&
      props.source?.type === 'ARTIST'
    ) {
      props.setShowError &&
        props.setShowError(<Translate id="ALREADY_EXISTS" />);
      return;
    }
    //Credits
    let findEmailCredit = props.currentValues?.some(
      (val) => val.email === contact?.value
    );
    let findTelephoneCredit = props.currentValues?.some(
      (val) => val.telephone === contact?.value
    );
    if (
      (findEmailCredit || findTelephoneCredit) &&
      props?.source?.type === 'CONTRIBUTOR'
    ) {
      props.setShowError &&
        props.setShowError(<Translate id="ALREADY_EXISTS" />);
      return;
    }
    //collaborators
    let findEmailCollaborators = props.currentValues?.some(
      (val) => val.email === contact?.value
    );
    let findTelephoneCollaborators = props.currentValues?.some(
      (val) => val.contact === contact?.value
    );
    if (
      (findEmailCollaborators || findTelephoneCollaborators) &&
      props?.source?.type === 'COLLABORATOR'
    ) {
      props.setShowError &&
        props.setShowError(<Translate id="ALREADY_EXISTS" />);
      return;
    }

    props.setShowError && props.setShowError(undefined);
    if (!role) {
      props.setShowError &&
        props.setShowError(<Translate id="ALREADY_EXISTS" />);

      return;
    }

    try {
      if (props.type !== 'operator') {
        const inviter = props.fromUser ? props.activeUser : props.activeArtist;
        if (inviter) {
          const invite: InviteArtistRequest = {
            type: props.type,
            contactType: contact.type,
            invitee: contact.value,
            artist: {
              name: props.name,
            },
            inviter,
            role,
            songName: props.songName,
            split: props.split,
            recordLabel: {
              name: props.labelName,
              id: props.labelId,
              plan: props.labelPlan,
            },
            releaseId: props.releaseId,
            value: props.value,
          };
          props.inviteArtist(invite, props.source);
          props.onInvite();
        }
      } else if (props.labelId && props.inviter) {
        const invite = {
          inviter: props.inviter,
          contactType: contact.type,
          address: contact.value,
        };
        props.inviteOperator(props.labelId, invite);
        props.onInvite();
      }
    } catch (e) {
      props.setShowError &&
        props.setShowError(<Translate id={(e as Error).message} />);
    }
  };
  return (
    <div className={props.className}>
      <StyledEmailOrPhone
        customBorderRadius={props.customBorderRadius}
        value={contact?.value}
        country={country}
        onChange={(newContact) => {
          setContact(newContact);
          if (props?.handleEmailOrPhoneChange)
            props.handleEmailOrPhoneChange(newContact);
          if (props.setShowError) props.setShowError(false);
        }}
        onCountryChange={(c) => setCountry(c)}
        onInputChange={(e) => {
          setContactFieldValue(e);
        }}
        error={props.showError ? props?.showError : undefined}
      />
      <Flex gap={5} className="mt-10">
        <Button buttonType="secondary" onClick={props.onCancel} fullWidth>
          <Translate id="RELEASE.NAMES_GENRES.MAIN_ARTISTS.LABEL.BUTTON.CANCEL" />
        </Button>
        <Button
          disabled={
            (props.canBeWithoutInvite &&
              contactFieldValue &&
              !contact?.value) ||
            (!props.canBeWithoutInvite && !contactFieldValue) ||
            (!props.canBeWithoutInvite && !contact?.value)
              ? true
              : false
          }
          buttonType="secondary-dark"
          onClick={handleInvite}
          fullWidth
        >
          <Translate
            id={
              props.canBeWithoutInvite && !contactFieldValue
                ? 'RELEASE.NAMES_GENRES.MAIN_ARTISTS.LABEL.BUTTON.ADD'
                : 'RELEASE.NAMES_GENRES.MAIN_ARTISTS.LABEL.BUTTON.INVITE'
            }
          />
        </Button>
      </Flex>
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InviteArtistInlineCtrl);

const StyledEmailOrPhone = styled(EmailOrPhoneCtrl)`
  .inline-button {
    width: auto;
    padding: 8px 14px;
    min-height: 32px !important;
    min-width: 68px;
  }
`;
